import { Link, useParams } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, GraduationCap } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import CTASection from "@/components/site/CTASection";
import NotFound from "@/pages/NotFound";
import { usePageMeta } from "@/components/site/SEO";
import { getJourney } from "@/content/journeys";
import { SAFETY_COPY } from "@/content/site";

const FACT_ICONS = [GraduationCap, Clock, CalendarDays];

export default function JourneyPage() {
  const { slug } = useParams();
  const journey = getJourney(slug);
  usePageMeta(
    journey ? journey.meta.title : "Journey",
    journey ? journey.meta.description : undefined,
  );
  if (!journey) return <NotFound />;

  return (
    <>
      <section className="relative flex min-h-[72vh] items-end overflow-hidden bg-pineink text-canvas" data-testid="journey-hero">
        <img
          src={journey.image.src}
          alt={journey.image.alt}
          fetchPriority="high"
          style={{ objectPosition: journey.image.position || "center" }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" aria-hidden="true" />
        <div className="container-x relative z-10 pb-16 pt-44 sm:pb-20">
          <p className="mb-5 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.24em] text-canvas/85">
            <span className="inline-block h-px w-10 bg-[#CFC286]" aria-hidden="true" />
            {journey.destination} · For schools
          </p>
          <h1 className="max-w-3xl text-4xl font-medium leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
            {journey.title}
          </h1>
          <p className="mt-6 max-w-xl font-serif text-xl italic leading-relaxed text-canvas/90 sm:text-2xl">
            {journey.positioning}
          </p>
          {journey.tagline && (
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#CFC286]">
              {journey.tagline}
            </p>
          )}
        </div>
      </section>

      {journey.quickFacts?.length > 0 && (
        <section className="border-b border-line bg-white" data-testid="journey-quick-facts">
          <div className="container-x grid gap-px sm:grid-cols-3">
            {journey.quickFacts.map((fact, i) => {
              const Icon = FACT_ICONS[i % FACT_ICONS.length];
              return (
                <div key={fact.label} className="flex items-start gap-4 py-7 sm:px-6 sm:first:pl-0">
                  <Icon className="mt-1 h-5 w-5 shrink-0 text-brass" aria-hidden="true" />
                  <div>
                    <p className="eyebrow">{fact.label}</p>
                    <p className="mt-1.5 text-lg font-medium text-ink">{fact.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {journey.overview?.length > 0 && (
        <section className="container-x py-20 sm:py-24" data-testid="journey-overview">
          <div className="grid gap-8 lg:grid-cols-[280px_1fr] lg:gap-20">
            <Reveal>
              <h2 className="text-2xl font-medium tracking-tight sm:text-3xl">Overview</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="max-w-2xl space-y-6">
                {journey.overview.map((paragraph, i) => (
                  <p key={i} className="text-lg leading-relaxed text-ink/85">
                    {paragraph}
                  </p>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {journey.whyThisPlace && (
        <section className="border-t border-line bg-white" data-testid="journey-why-this-place">
          <div className="container-x grid items-center gap-12 py-20 sm:py-24 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <p className="eyebrow mb-5">Why this place</p>
              <h2 className="max-w-lg text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                {journey.whyThisPlace.heading}
              </h2>
              <div className="mt-7 max-w-xl space-y-6">
                {journey.whyThisPlace.body.map((paragraph, i) => (
                  <p key={i} className="text-lg leading-relaxed text-ink/85">
                    {paragraph}
                  </p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <img
                src={(journey.detailImage || journey.image).src}
                alt={(journey.detailImage || journey.image).alt}
                loading="lazy"
                className="aspect-[4/3] w-full rounded-2xl object-cover"
              />
            </Reveal>
          </div>
        </section>
      )}

      {journey.themes?.length > 0 && (
        <section className="container-x py-20 sm:py-24" data-testid="journey-themes">
          <Reveal>
            <p className="eyebrow mb-5">Themes</p>
            <h2 className="max-w-xl text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              What this journey explores
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-x-12 sm:grid-cols-2 lg:grid-cols-3">
            {journey.themes.map((theme, i) => (
              <Reveal key={theme} delay={i * 0.05}>
                <div className="flex items-baseline gap-4 border-t border-line py-5">
                  <span className="font-serif text-lg text-brass" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-lg font-medium text-ink">{theme}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {journey.activities?.length > 0 && (
        <section className="border-t border-line" data-testid="journey-activities">
          <div className="container-x py-20 sm:py-24">
            <Reveal>
              <p className="eyebrow mb-5">Experiences</p>
              <h2 className="max-w-xl text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                What students will do
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {journey.activities.map((activity, i) => (
                <Reveal key={activity.title} delay={i * 0.04}>
                  <div className="border-t border-line pt-5">
                    <h3 className="text-lg font-semibold text-ink">{activity.title}</h3>
                    <p className="mt-2 text-base leading-relaxed text-ink/70">{activity.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            {journey.ancillary?.length > 0 && (
              <Reveal>
                <div className="mt-14 border border-line bg-white p-8 sm:p-10">
                  <p className="eyebrow mb-6">Ancillary experiences</p>
                  <div className="grid gap-x-12 gap-y-8 sm:grid-cols-3">
                    {journey.ancillary.map((activity) => (
                      <div key={activity.title}>
                        <h3 className="text-base font-semibold text-ink">{activity.title}</h3>
                        <p className="mt-2 text-base leading-relaxed text-ink/70">{activity.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {journey.outcomes?.length > 0 && (
        <section className="bg-pine text-canvas" data-testid="journey-outcomes">
          <div className="container-x py-20 sm:py-24">
            <Reveal>
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.22em] text-[#CFC286]">
                Learning through experience
              </p>
              <h2 className="max-w-xl text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                What students take home
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {journey.outcomes.map((outcome, i) => (
                <Reveal key={outcome.title} delay={i * 0.04}>
                  <div className="border-t border-canvas/20 pt-5">
                    <h3 className="text-lg font-medium text-canvas">{outcome.title}</h3>
                    <p className="mt-2 text-base leading-relaxed text-canvas/75">{outcome.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {journey.inclusions?.length > 0 && (
        <section className="border-t border-line bg-white" data-testid="journey-inclusions">
          <div className="container-x py-20 sm:py-24">
            <div className="grid gap-10 lg:grid-cols-[280px_1fr] lg:gap-20">
              <Reveal>
                <div>
                  <p className="eyebrow mb-5">What is included</p>
                  <h2 className="text-2xl font-medium tracking-tight sm:text-3xl">
                    Taken care of, end to end
                  </h2>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <ul className="grid gap-x-12 sm:grid-cols-2">
                  {journey.inclusions.map((item) => (
                    <li key={item} className="flex items-baseline gap-3 border-t border-line py-4 text-base text-ink/85">
                      <span className="h-1.5 w-1.5 shrink-0 translate-y-[-2px] rounded-full bg-brass" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-line bg-white" data-testid="journey-safety">
        <div className="container-x py-20 sm:py-24">
          <Reveal>
            <div className="max-w-2xl">
              <p className="eyebrow mb-5">{SAFETY_COPY.heading}</p>
              {journey.safety?.length > 0 ? (
                <div className="space-y-6">
                  {journey.safety.map((paragraph, i) => (
                    <p key={i} className="text-lg leading-relaxed text-ink/85">
                      {paragraph}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-lg leading-relaxed text-ink/85">{SAFETY_COPY.home}</p>
              )}
              <Link
                to="/our-approach#safety"
                data-testid="journey-safety-link"
                className="mt-7 inline-flex items-center gap-2 text-base font-medium text-pine underline decoration-pine/40 underline-offset-8 transition-colors hover:decoration-pine"
              >
                More about safety and our approach
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        heading={`Bring ${journey.title} to your school`}
        text="Tell us about your students and what you would like them to discover. We will plan the journey together."
        primary={{
          label: "Enquire about this journey",
          to: `/start-your-journey?journey=${journey.slug}`,
          testId: "journey-enquiry-cta",
        }}
        secondary={{ label: "Explore all journeys", to: "/journeys", testId: "journey-all-journeys-cta" }}
        testId="journey-cta"
      />
    </>
  );
}
